import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import {
  fetchItDeclarationList,
  fetchItDeclarationTypes,
  fetchItDeclarationRequestPhase,
  fetchItDeclarationRequestStatus,
  syncItDeclaration,
  ItDeclarationItem,
} from "../../../core/data/redux/itDeclarationSlice";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { toast } from "react-toastify";

import { getStatusBadge } from "./StatusBadge";

const routes = all_routes;

// Format YYYY-MM-DD input date to DD.MM.YYYY required by SAP find-all API
const formatYmdToDmy = (ymd: string): string => {
  if (!ymd) return "";
  const parts = ymd.split("-");
  if (parts.length === 3) {
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }
  return ymd;
};

const REQUEST_TYPE_OPTIONS = [
  { value: "All", label: "All Request Types" },
  { value: "Application Cancellation", label: "Application Cancellation" },
  { value: "Advance", label: "Advance" },
  { value: "Approval Application", label: "Approval Application" },
  { value: "Application", label: "Application" },
  { value: "Advance Repayment", label: "Advance Repayment" },
  { value: "Claim Against Advance", label: "Claim Against Advance" },
  { value: "Claim", label: "Claim" },
  { value: "Claim Against Application", label: "Claim Against Application" },
  { value: "Encashment", label: "Encashment" },
  { value: "Geneic Application", label: "Geneic Application" },
];

const ITDeclarationList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { list, declarationTypes, phaseOptions, statusOptions, loading, syncing } = useSelector(
    (state: RootState) => state.itDeclarationState
  );

  const [referenceNumber, setReferenceNumber] = useState("");
  const [selectedDeclarationType, setSelectedDeclarationType] = useState("All");
  const [selectedRequestType, setSelectedRequestType] = useState("All");
  const [selectedRequestPhase, setSelectedRequestPhase] = useState("All");
  const [selectedRequestStatus, setSelectedRequestStatus] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterExpanded, setFilterExpanded] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [createConfirmModalOpen, setCreateConfirmModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchItDeclarationList({
      page: 1,
      limit: pageSize,
      orderBy: "DESC",
      orderColumn: "ReferenceNumber",
    }));
    dispatch(fetchItDeclarationTypes({ forceSync: false }));
    dispatch(fetchItDeclarationRequestPhase());
    dispatch(fetchItDeclarationRequestStatus());
  }, [dispatch]);

  const handleSync = async () => {
    try {
      const res = await dispatch(syncItDeclaration()).unwrap();
      if (res?.success || res?.data?.success !== false) {
        toast.success("Synced successfully");
      } else {
        toast.error(res?.message || res?.data?.message || "Sync failed");
      }
    } catch (err: any) {
      toast.error(err?.message || "Sync failed");
    } finally {
      handleApplyFilter();
    }
  };

  const declTypeLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(declarationTypes)) {
      declarationTypes.forEach((item: any) => {
        const code = String(item?.ITDeclarationType || item?.value || "");
        const text = String(item?.ITDeclarationTypeText || item?.label || "");
        if (code && text) map[code] = text;
      });
    }
    return map;
  }, [declarationTypes]);

  const summaryStats = useMemo(() => {
    return {
      totalClaims: list.length,
      inProgressClaims: list.filter((r) => r.status === "In-Process").length,
      completedClaims: list.filter((r) => r.status === "Completed" || r.status === "Approved" || r.statusCode === "A").length,
    };
  }, [list]);

  const handleApplyFilter = () => {
    const payload: any = {
      page: 1,
      limit: pageSize,
      orderBy: "DESC",
      orderColumn: "ReferenceNumber",
    };
    if (referenceNumber.trim()) payload.referenceNumber = referenceNumber.trim();
    if (selectedDeclarationType !== "All") payload.declarationType = selectedDeclarationType;
    if (selectedRequestType !== "All") payload.requestType = selectedRequestType;
    if (selectedRequestPhase !== "All") payload.requestPhase = selectedRequestPhase;
    if (selectedRequestStatus !== "All") payload.requestStatus = selectedRequestStatus;
    if (dateFrom) payload.creationDateFrom = formatYmdToDmy(dateFrom);
    if (dateTo) payload.creationDateTo = formatYmdToDmy(dateTo);

    setCurrentPage(1);
    dispatch(fetchItDeclarationList(payload));
  };

  const handleResetFilter = () => {
    setReferenceNumber("");
    setSelectedDeclarationType("All");
    setSelectedRequestType("All");
    setSelectedRequestPhase("All");
    setSelectedRequestStatus("All");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    dispatch(fetchItDeclarationList({
      page: 1,
      limit: pageSize,
      orderBy: "DESC",
      orderColumn: "ReferenceNumber",
    }));
  };

  const declTypeSelectOptions = useMemo(() => {
    const opts = [{ value: "All", label: "All Declaration Types" }];
    if (Array.isArray(declarationTypes)) {
      declarationTypes.forEach((item: any) => {
        const val = item?.ITDeclarationType || item?.value || "";
        const lbl = item?.ITDeclarationTypeText || item?.label || val;
        if (val) opts.push({ value: val, label: `${lbl} (${val})` });
      });
    }
    return opts;
  }, [declarationTypes]);

  const phaseSelectOptions = useMemo(() => {
    const opts = [{ value: "All", label: "All Phases" }];
    if (Array.isArray(phaseOptions)) {
      phaseOptions.forEach((item: any) => {
        const val = item?.RequestPhase || item?.code || item?.value || "";
        const lbl = item?.RequestPhaseText || item?.text || item?.label || val;
        if (val) opts.push({ value: val, label: lbl });
      });
    }
    return opts;
  }, [phaseOptions]);

  const statusSelectOptions = useMemo(() => {
    const opts = [{ value: "All", label: "All Statuses" }];
    if (Array.isArray(statusOptions)) {
      statusOptions.forEach((item: any) => {
        const val = item?.RequestStatus || item?.code || item?.value || "";
        const lbl = item?.RequestStatusText || item?.text || item?.label || val;
        if (val) opts.push({ value: val, label: lbl });
      });
    }
    return opts;
  }, [statusOptions]);

  const filteredList = useMemo(() => {
    if (selectedRequestType === "All") return list;
    return list.filter((r) => {
      const rt = String(r.requestType || "").trim().toLowerCase();
      return rt === selectedRequestType.trim().toLowerCase();
    });
  }, [list, selectedRequestType]);

  // Paginated records
  const paginatedList = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredList.slice(startIndex, startIndex + pageSize);
  }, [filteredList, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredList.length / pageSize) || 1;

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Breadcrumb Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2 mb-md-0">
            <h3 className="page-title mb-1 text-dark fw-bold">IT Declarations - India</h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard} className="text-primary text-decoration-none">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  IT Declarations
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-primary d-flex align-items-center me-1 shadow-sm"
              onClick={handleSync}
              disabled={syncing}
              title="Sync IT Declarations"
            >
              <i className={`ti ti-refresh me-1.5 fs-16 ${syncing ? "spin" : ""}`} />
              {syncing ? "Syncing..." : "Sync"}
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center shadow-sm"
              onClick={() => setCreateConfirmModalOpen(true)}
            >
              <i className="ti ti-circle-plus me-1.5 fs-16" />
              Create IT Declaration
            </button>
            <CollapseHeader />
          </div>
        </div>

        {/* Summary Metric Cards */}
        <div className="row mb-3 g-3">
          <div className="col-xl-4 col-md-4 col-sm-12">
            <div className="card shadow-sm border-0 mb-0 h-100">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1 fs-13 fw-medium">Total Declarations</h6>
                  <h3 className="fw-bold mb-0 text-dark">{summaryStats.totalClaims}</h3>
                </div>
                <div
                  className="avatar avatar-md rounded-circle d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}
                >
                  <i className="ti ti-file-text fs-22" />
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-md-4 col-sm-12">
            <div className="card shadow-sm border-0 mb-0 h-100">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1 fs-13 fw-medium">In-Process Claims</h6>
                  <h3 className="fw-bold mb-0 text-dark">{summaryStats.inProgressClaims}</h3>
                </div>
                <div
                  className="avatar avatar-md rounded-circle d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "#fef3c7", color: "#d97706" }}
                >
                  <i className="ti ti-clock-hour-4 fs-22" />
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-md-4 col-sm-12">
            <div className="card shadow-sm border-0 mb-0 h-100">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-muted mb-1 fs-13 fw-medium">Completed Claims</h6>
                  <h3 className="fw-bold mb-0 text-dark">{summaryStats.completedClaims}</h3>
                </div>
                <div
                  className="avatar avatar-md rounded-circle d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
                >
                  <i className="ti ti-circle-check fs-22" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Criteria Maintenance / Filter Card */}
        <div className="card mb-3 shadow-sm border-0">
          <div
            className="card-header bg-white border-bottom d-flex align-items-center justify-content-between p-3"
            style={{ cursor: "pointer" }}
            onClick={() => setFilterExpanded(!filterExpanded)}
          >
            <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ti ti-adjustments-horizontal text-primary fs-18" />
              Quick Criteria Maintenance
            </h6>
            <button
              type="button"
              className="btn btn-sm btn-link text-muted p-0"
              onClick={(e) => {
                e.stopPropagation();
                setFilterExpanded(!filterExpanded);
              }}
            >
              <i className={`ti ti-chevron-${filterExpanded ? "up" : "down"} fs-18`} />
            </button>
          </div>
          {filterExpanded && (
            <div className="card-body p-3">
              <div className="row g-3">
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">Reference Number</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Enter reference number"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">IT - Declaration</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedDeclarationType}
                    onChange={(e) => setSelectedDeclarationType(e.target.value)}
                  >
                    {declTypeSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">Request Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedRequestType}
                    onChange={(e) => setSelectedRequestType(e.target.value)}
                  >
                    {REQUEST_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">Request Phase</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedRequestPhase}
                    onChange={(e) => setSelectedRequestPhase(e.target.value)}
                  >
                    {phaseSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">Request Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedRequestStatus}
                    onChange={(e) => setSelectedRequestStatus(e.target.value)}
                  >
                    {statusSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">Creation Date From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">Creation Date To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="col-lg-6 col-12 d-flex align-items-end justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary px-3 d-inline-flex align-items-center"
                    onClick={handleApplyFilter}
                  >
                    <i className="ti ti-filter me-1" /> Apply
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary px-3 d-inline-flex align-items-center"
                    onClick={handleResetFilter}
                  >
                    <i className="ti ti-refresh me-1" /> Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Declarations List Table */}
        <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
          <div className="card-header bg-white border-bottom p-3 d-flex align-items-center justify-content-between">
            <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ti ti-list-details text-primary fs-18" />
              Declarations List
            </h6>
            <span className="badge bg-light text-muted border px-2.5 py-1 fs-12">
              Showing {list.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} -{" "}
              {Math.min(currentPage * pageSize, list.length)} of {list.length}
            </span>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted fs-13 mt-2 mb-0">Loading IT Declaration requests...</p>
              </div>
            ) : list.length === 0 ? (
              <div className="text-center p-5 text-muted">
                <i className="ti ti-file-x fs-48 text-muted mb-2 d-block" />
                <h6 className="fw-semibold text-dark mb-1">No requests available for the selected filters.</h6>
                <p className="fs-13 text-muted mb-3">Adjust your search parameters or create a new IT Declaration request.</p>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => setCreateConfirmModalOpen(true)}
                >
                  <i className="ti ti-plus me-1" /> Create Request
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 fs-13">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold text-dark">Reference No</th>
                      <th className="fw-semibold text-dark">Employee Number</th>
                      <th className="fw-semibold text-dark">IT - Declaration Type</th>
                      <th className="fw-semibold text-dark">Creation Date</th>
                      <th className="fw-semibold text-dark">Employee Name</th>
                      <th className="fw-semibold text-dark">Request Phase</th>
                      <th className="fw-semibold text-dark">Request Status</th>
                      <th className="fw-semibold text-dark">Approver</th>
                      <th className="fw-semibold text-dark">Tax Exemption Approved</th>
                      <th className="text-end fw-semibold text-dark" style={{ minWidth: "140px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedList.map((item: ItDeclarationItem, idx: number) => (
                      <tr key={item.id || idx}>
                        <td className="fw-bold text-primary">
                          <Link
                            to={`/it-declaration/details/${item.referenceNumber || item.id}`}
                            className="text-decoration-none text-primary"
                          >
                            {item.referenceNumber || item.id}
                          </Link>
                        </td>
                        <td className="fw-medium text-dark">{item.employeeName || item.raw?.EmpID || "-"}</td>
                        <td>
                          <span className="fw-medium text-dark">
                            {declTypeLabelMap[item.declarationType] || item.declarationType || "-"}
                          </span>
                        </td>
                        <td className="text-muted">{item.creationDate || "-"}</td>
                        <td className="fw-medium text-dark">{item.employeeName || "-"}</td>
                        <td>
                          <span className="text-dark">{item.requestPhase || item.status || "-"}</span>
                        </td>
                        <td>
                          {getStatusBadge(item.statusCode || item.status)}
                        </td>
                        <td className="text-muted">{item.approver || "-"}</td>
                        <td>
                          <span className="fw-medium text-dark">
                            {item.taxExemptApproved ? `₹${item.taxExemptApproved}` : "-"}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary px-2.5 py-1 d-inline-flex align-items-center"
                            onClick={() => navigate(`/it-declaration/details/${item.referenceNumber || item.id}`)}
                            title="Display Request"
                          >
                            <i className="ti ti-eye me-1" /> Display Request
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {!loading && list.length > pageSize && (
            <div className="card-footer bg-white border-top p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span className="text-muted fs-13">
                Page {currentPage} of {totalPages} ({list.length} total items)
              </span>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && p - arr[i - 1] > 1 && (
                        <li className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      )}
                      <li className={`page-item ${currentPage === p ? "active" : ""}`}>
                        <button className="page-link" onClick={() => setCurrentPage(p)}>
                          {p}
                        </button>
                      </li>
                    </React.Fragment>
                  ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Create New Request Confirmation Modal (exact to mobile text & confirmation) */}
        {createConfirmModalOpen && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "420px" }}>
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-light border-bottom p-3">
                  <h5 className="modal-title fw-bold text-dark fs-16">Create New Request</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setCreateConfirmModalOpen(false)}
                  />
                </div>
                <div className="modal-body p-4 text-center">
                  <div
                    className="avatar avatar-lg rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}
                  >
                    <i className="ti ti-file-plus fs-28" />
                  </div>
                  <h6 className="fw-bold text-dark mb-2">Create New IT Declaration</h6>
                  <p className="fs-14 text-muted mb-0">
                    Would you like to create a new reimbursement request?
                  </p>
                </div>
                <div className="modal-footer bg-light border-top justify-content-center p-3 gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4 py-1.5 fs-13"
                    onClick={() => setCreateConfirmModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4 py-1.5 fs-13 fw-semibold shadow-sm"
                    onClick={() => {
                      setCreateConfirmModalOpen(false);
                      navigate(routes.itDeclaration.create);
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ITDeclarationList;
